import React, {useState, useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {Close, DesktopWindows, Tablet, Phone} from '@mui/icons-material';
import MDTypography from 'components/MDTypography';
import {Block} from 'notiflix';
import sectionContentApi from 'api/sectionContentApi';

function SectionPreviewModal({open, onClose, sectionId, formData}) {
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState('desktop'); // desktop, tablet, mobile

  useEffect(() => {
    if (open && sectionId) {
      fetchPreview();
    }
  }, [open, sectionId, formData]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      Block.circle('#preview-loading', 'Generating preview...');

      const response = await sectionContentApi.previewSection(
        sectionId,
        formData,
      );
      setPreviewHtml(response.html || response);
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setLoading(false);
      Block.remove('#preview-loading');
    }
  };

  const getPreviewWidth = () => {
    switch (device) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      default:
        return '100%';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {height: '90vh'},
      }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6">Section Preview</MDTypography>
          <Box display="flex" gap={1}>
            <Tooltip title="Desktop View">
              <IconButton
                color={device === 'desktop' ? 'primary' : 'default'}
                onClick={() => setDevice('desktop')}>
                <DesktopWindows />
              </IconButton>
            </Tooltip>
            <Tooltip title="Tablet View">
              <IconButton
                color={device === 'tablet' ? 'primary' : 'default'}
                onClick={() => setDevice('tablet')}>
                <Tablet />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mobile View">
              <IconButton
                color={device === 'mobile' ? 'primary' : 'default'}
                onClick={() => setDevice('mobile')}>
                <Phone />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          id="preview-loading"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
          }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box
              sx={{
                width: getPreviewWidth(),
                margin: '0 auto',
                transition: 'width 0.3s ease',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'white',
              }}>
              {previewHtml ? (
                <div dangerouslySetInnerHTML={{__html: previewHtml}} />
              ) : (
                <Box textAlign="center" py={4}>
                  <MDTypography color="text">No preview available</MDTypography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SectionPreviewModal;

// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   Box,
//   CircularProgress,
//   IconButton,
//   Tooltip,
//   Typography,
//   Chip,
// } from '@mui/material';
// import {
//   Close,
//   DesktopWindows,
//   Tablet,
//   Phone,
//   Refresh,
//   OpenInNew,
// } from '@mui/icons-material';
// import MDTypography from 'components/MDTypography';
// import sectionContentApi from 'api/sectionContentApi';

// const DEVICE_CONFIG = {
//   desktop: {
//     width: '100%',
//     label: 'Desktop',
//     minWidth: null,
//   },
//   tablet: {
//     width: '768px',
//     label: 'Tablet (768px)',
//     minWidth: '768px',
//   },
//   mobile: {
//     width: '375px',
//     label: 'Mobile (375px)',
//     minWidth: '375px',
//   },
// };

// function SectionPreviewModal({ open, onClose, sectionId, formData }) {
//   const [previewHtml, setPreviewHtml] = useState('');
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [device, setDevice] = useState('desktop');
//   const [iframeHeight, setIframeHeight] = useState(600);
//   const iframeRef = useRef(null);

//   useEffect(() => {
//     if (open && sectionId) {
//       fetchPreview();
//     }
//     // Reset state on close
//     if (!open) {
//       setError(null);
//     }
//   }, [open, sectionId]);

//   // Listen for height messages from iframe
//   useEffect(() => {
//     const handleMessage = (event) => {
//       if (event.data?.type === 'PREVIEW_HEIGHT') {
//         setIframeHeight(Math.max(event.data.height + 40, 300));
//       }
//     };
//     window.addEventListener('message', handleMessage);
//     return () => window.removeEventListener('message', handleMessage);
//   }, []);

//   const fetchPreview = async () => {
//     try {
//       setLoading(true);
//       setError(null);

//       const response = await sectionContentApi.previewSection(sectionId, formData);
//       const html = response.html || response;

//       // Inject the height-reporting script into the HTML
//       const heightScript = `
//         <script>
//           function reportHeight() {
//             const height = document.documentElement.scrollHeight || document.body.scrollHeight;
//             window.parent.postMessage({ type: 'PREVIEW_HEIGHT', height: height }, '*');
//           }
//           window.addEventListener('load', reportHeight);
//           // Also report after images/fonts load
//           window.addEventListener('resize', reportHeight);
//           // Observe DOM mutations (dynamic content)
//           const observer = new MutationObserver(reportHeight);
//           observer.observe(document.body, { childList: true, subtree: true, attributes: true });
//         </script>
//       `;

//       // Insert script before </body> or at end if no body tag
//       let finalHtml = html;
//       if (html.includes('</body>')) {
//         finalHtml = html.replace('</body>', `${heightScript}</body>`);
//       } else if (html.includes('</html>')) {
//         finalHtml = html.replace('</html>', `${heightScript}</html>`);
//       } else {
//         finalHtml = html + heightScript;
//       }

//       // If the response is a fragment (no <html> tag), wrap it in a full document
//       if (!finalHtml.includes('<html') && !finalHtml.includes('<!DOCTYPE')) {
//         finalHtml = wrapInFullDocument(finalHtml);
//       }

//       setPreviewHtml(finalHtml);
//     } catch (err) {
//       console.error('Preview error:', err);
//       setError(err.message || 'Failed to load preview');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Wrap an HTML fragment in a complete document so styles load correctly
//   const wrapInFullDocument = (fragment) => {
//     return `<!DOCTYPE html>
// <html lang="en">
// <head>
//   <meta charset="UTF-8" />
//   <meta name="viewport" content="width=device-width, initial-scale=1.0" />
//   <title>Section Preview</title>
//   <!-- Inherit stylesheets from parent page (same origin only) -->
//   ${getParentStylesheets()}
//   <style>
//     *, *::before, *::after { box-sizing: border-box; }
//     body { margin: 0; padding: 0; }
//   </style>
// </head>
// <body>
//   ${fragment}
//   <script>
//     function reportHeight() {
//       const height = document.documentElement.scrollHeight || document.body.scrollHeight;
//       window.parent.postMessage({ type: 'PREVIEW_HEIGHT', height: height }, '*');
//     }
//     window.addEventListener('load', reportHeight);
//     window.addEventListener('resize', reportHeight);
//     const observer = new MutationObserver(reportHeight);
//     document.addEventListener('DOMContentLoaded', () => {
//       observer.observe(document.body, { childList: true, subtree: true, attributes: true });
//       reportHeight();
//     });
//   </script>
// </body>
// </html>`;
//   };

//   // Collect <link rel="stylesheet"> hrefs from the parent page (same origin)
//   const getParentStylesheets = () => {
//     try {
//       const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
//       return links
//         .map((l) => `<link rel="stylesheet" href="${l.href}" />`)
//         .join('\n  ');
//     } catch {
//       return '';
//     }
//   };

//   // Write HTML to iframe via srcdoc (avoids cross-origin issues with blob URLs)
//   const handleIframeLoad = useCallback(() => {
//     const iframe = iframeRef.current;
//     if (!iframe || !previewHtml) return;

//     try {
//       // Try to access contentDocument (same origin)
//       const doc = iframe.contentDocument || iframe.contentWindow?.document;
//       if (doc) {
//         doc.open();
//         doc.write(previewHtml);
//         doc.close();
//       }
//     } catch (e) {
//       // Cross-origin fallback – use srcdoc (already set)
//     }
//   }, [previewHtml]);

//   const deviceConfig = DEVICE_CONFIG[device];

//   return (
//     <Dialog
//       open={open}
//       onClose={onClose}
//       maxWidth="xl"
//       fullWidth
//       PaperProps={{
//         sx: {
//           height: '95vh',
//           maxHeight: '95vh',
//           display: 'flex',
//           flexDirection: 'column',
//           bgcolor: '#f5f5f5',
//         },
//       }}>
//       {/* ===== HEADER ===== */}
//       <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white', py: 1.5 }}>
//         <Box display="flex" justifyContent="space-between" alignItems="center">
//           <Box display="flex" alignItems="center" gap={1.5}>
//             <MDTypography variant="h6">Section Preview</MDTypography>
//             <Chip
//               label={deviceConfig.label}
//               size="small"
//               variant="outlined"
//               color="primary"
//               sx={{ fontSize: '0.7rem' }}
//             />
//           </Box>

//           <Box display="flex" alignItems="center" gap={0.5}>
//             {/* Device switcher */}
//             <Box
//               sx={{
//                 display: 'flex',
//                 border: '1px solid',
//                 borderColor: 'divider',
//                 borderRadius: 1,
//                 overflow: 'hidden',
//                 mr: 1,
//               }}>
//               {Object.entries(DEVICE_CONFIG).map(([key, cfg]) => (
//                 <Tooltip key={key} title={cfg.label}>
//                   <IconButton
//                     size="small"
//                     onClick={() => setDevice(key)}
//                     sx={{
//                       borderRadius: 0,
//                       px: 1.5,
//                       bgcolor: device === key ? 'primary.main' : 'transparent',
//                       color: device === key ? 'white' : 'inherit',
//                       '&:hover': {
//                         bgcolor: device === key ? 'primary.dark' : 'action.hover',
//                       },
//                     }}>
//                     {key === 'desktop' && <DesktopWindows fontSize="small" />}
//                     {key === 'tablet' && <Tablet fontSize="small" />}
//                     {key === 'mobile' && <Phone fontSize="small" />}
//                   </IconButton>
//                 </Tooltip>
//               ))}
//             </Box>

//             <Tooltip title="Refresh preview">
//               <IconButton size="small" onClick={fetchPreview} disabled={loading}>
//                 <Refresh fontSize="small" />
//               </IconButton>
//             </Tooltip>

//             <IconButton size="small" onClick={onClose}>
//               <Close fontSize="small" />
//             </IconButton>
//           </Box>
//         </Box>
//       </DialogTitle>

//       {/* ===== CONTENT ===== */}
//       <DialogContent
//         sx={{
//           flex: 1,
//           overflow: 'auto',
//           display: 'flex',
//           alignItems: 'flex-start',
//           justifyContent: 'center',
//           p: 2,
//           bgcolor: '#e8e8e8',
//         }}>
//         {loading ? (
//           <Box
//             display="flex"
//             flexDirection="column"
//             alignItems="center"
//             justifyContent="center"
//             sx={{ minHeight: 400, width: '100%' }}
//             gap={2}>
//             <CircularProgress size={40} />
//             <MDTypography variant="body2" color="text">
//               Generating preview...
//             </MDTypography>
//           </Box>
//         ) : error ? (
//           <Box
//             display="flex"
//             flexDirection="column"
//             alignItems="center"
//             justifyContent="center"
//             sx={{ minHeight: 400, width: '100%' }}
//             gap={2}>
//             <MDTypography variant="body2" color="error">
//               {error}
//             </MDTypography>
//             <Button variant="outlined" size="small" onClick={fetchPreview} startIcon={<Refresh />}>
//               Retry
//             </Button>
//           </Box>
//         ) : (
//           /* ---- Responsive iframe container ---- */
//           <Box
//             sx={{
//               width: deviceConfig.width,
//               minWidth: deviceConfig.minWidth || 'auto',
//               maxWidth: '100%',
//               transition: 'width 0.3s ease',
//               boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
//               borderRadius: 1,
//               overflow: 'hidden',
//               bgcolor: 'white',
//               // Show a "device frame" feel for mobile/tablet
//               ...(device !== 'desktop' && {
//                 border: '8px solid #222',
//                 borderRadius: 3,
//               }),
//             }}>
//             <iframe
//               ref={iframeRef}
//               title="Section Preview"
//               srcDoc={previewHtml}
//               onLoad={handleIframeLoad}
//               style={{
//                 display: 'block',
//                 width: '100%',
//                 height: iframeHeight,
//                 border: 'none',
//                 background: 'white',
//               }}
//               sandbox="allow-scripts allow-same-origin allow-popups"
//             />
//           </Box>
//         )}
//       </DialogContent>

//       {/* ===== FOOTER ===== */}
//       <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white', py: 1 }}>
//         <MDTypography variant="caption" color="text" sx={{ flex: 1, pl: 1 }}>
//           {previewHtml
//             ? 'Preview shows the section exactly as it appears on the frontend.'
//             : 'No preview available.'}
//         </MDTypography>
//         <Button onClick={fetchPreview} variant="outlined" size="small" startIcon={<Refresh />} disabled={loading}>
//           Refresh
//         </Button>
//         <Button onClick={onClose} variant="contained" size="small">
//           Close
//         </Button>
//       </DialogActions>
//     </Dialog>
//   );
// }

// export default SectionPreviewModal;
